import {useCallback, useEffect, useRef} from "react";
import {useSearchParams} from "react-router";
import {APPLY_DATES_PARAM, DateRange} from "../../util/ReportDateRangeUtils";
import {SKIP_REMOUNT_STATE} from "../../components/hocs/withRemountOnQueryChange";

/**
 * Stable callback that writes the date range and apply-dates override to the URL in one replace, without remounting a
 * `withRemountOnQueryChange` view. Both dates are written (blank = All Dates) unless the range is the default, so a
 * reload never has to rebuild one date from the other.
 */
const usePersistDateRangeParams = (defaults: DateRange) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const latest = useRef({searchParams, setSearchParams});

  useEffect(() => {
    latest.current = {searchParams, setSearchParams};
  }, [searchParams, setSearchParams]);

  const {startDate: defaultStartDate, endDate: defaultEndDate} = defaults;

  return useCallback((range: DateRange, applyDates?: boolean) => {
    const {searchParams: current, setSearchParams: update} = latest.current;
    const next = new URLSearchParams(current);

    if (range.startDate === defaultStartDate && range.endDate === defaultEndDate) {
      next.delete('startDate');
      next.delete('endDate');
    } else {
      next.set('startDate', range.startDate);
      next.set('endDate', range.endDate);
    }
    if (applyDates) {
      next.set(APPLY_DATES_PARAM, 'true');
    } else {
      next.delete(APPLY_DATES_PARAM);
    }

    if (next.toString() !== current.toString()) {
      update(next, {replace: true, state: SKIP_REMOUNT_STATE});
    }
  }, [defaultStartDate, defaultEndDate]);
};

export {usePersistDateRangeParams};
